/**
 * EMZI NEXUS V6.4 - Live Studio V2
 * Step 7: Review & Prime SOA
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { EMZI_SPACING, EMZI_RADIUS } from '../../../styles/emziCI';
import * as api from '../../../api/liveStudioV2';

const Step7ReviewPrime = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { language } = useLanguage();
  const [isPriming, setIsPriming] = useState(false);
  const [isPrimed, setIsPrimed] = useState(false);
  const [soaPrime, setSoaPrime] = useState(sessionData?.soaPlan || null);

  const getText = (key) => {
    const translations = {
      en: {
        title: 'Review & Prime SOA',
        subtitle: 'Review your setup and generate SOA questions',
        summaryTitle: 'Session Summary',
        platformLabel: 'Platform',
        durationLabel: 'Duration',
        ctaIntervalLabel: 'CTA Interval',
        objectiveLabel: 'Objective',
        audienceLabel: 'Target Audience',
        panggilanLabel: 'Panggilan',
        nadaLabel: 'Nada',
        primeBtn: '✨ Prime SOA Questions',
        primingBtn: 'Generating questions...',
        continueBtn: 'Continue to SOA Drill →',
        questionsGenerated: 'questions generated',
        readyText: 'Ready for SOA Drill!'
      },
      ms: {
        title: 'Semak & Prime SOA',
        subtitle: 'Semak persediaan anda dan jana soalan SOA',
        summaryTitle: 'Ringkasan Sesi',
        platformLabel: 'Platform',
        durationLabel: 'Tempoh',
        ctaIntervalLabel: 'Selang CTA',
        objectiveLabel: 'Objektif',
        audienceLabel: 'Sasaran Audiens',
        panggilanLabel: 'Panggilan',
        nadaLabel: 'Nada',
        primeBtn: '✨ Prime Soalan SOA',
        primingBtn: 'Menjana soalan...',
        continueBtn: 'Teruskan ke SOA Drill →',
        questionsGenerated: 'soalan dijana',
        readyText: 'Sedia untuk SOA Drill!'
      },
      bm: {
        title: 'Semak & Prime SOA',
        subtitle: 'Semak persediaan anda dan jana soalan SOA',
        summaryTitle: 'Ringkasan Sesi',
        platformLabel: 'Platform',
        durationLabel: 'Tempoh',
        ctaIntervalLabel: 'Selang CTA',
        objectiveLabel: 'Objektif',
        audienceLabel: 'Sasaran Audiens',
        panggilanLabel: 'Panggilan',
        nadaLabel: 'Nada',
        primeBtn: '✨ Prime Soalan SOA',
        primingBtn: 'Menjana soalan...',
        continueBtn: 'Teruskan ke SOA Drill →',
        questionsGenerated: 'soalan dijana',
        readyText: 'Sedia untuk SOA Drill!'
      },
      urban: {
        title: 'Review & Prime SOA',
        subtitle: 'Review setup korang dan generate SOA questions',
        summaryTitle: 'Session Summary',
        platformLabel: 'Platform',
        durationLabel: 'Duration',
        ctaIntervalLabel: 'CTA Interval',
        objectiveLabel: 'Objective',
        audienceLabel: 'Target Audience',
        panggilanLabel: 'Panggilan',
        nadaLabel: 'Nada',
        primeBtn: '✨ Prime SOA Questions',
        primingBtn: 'Generating questions...',
        continueBtn: 'Continue ke SOA Drill →',
        questionsGenerated: 'questions generated',
        readyText: 'Ready untuk SOA Drill!'
      }
    };
    return translations[language]?.[key] || translations.en[key];
  };

  // Check if already primed
  useEffect(() => {
    if (sessionData?.soaPlan?.blueprint?.length > 0) {
      setSoaPrime(sessionData.soaPlan);
      setIsPrimed(true);
    }
  }, [sessionData]);

  // Prime SOA Questions
  const handlePrime = async () => {
    setIsPriming(true);
    setError(null);
    
    try {
      const result = await api.primeLiveSOA(token, sessionId);
      setSoaPrime(result.soa_prime);
      setIsPrimed(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsPriming(false);
    }
  };

  // Continue to SOA Drill
  const handleContinue = () => {
    if (!isPrimed) {
      setError('Please prime SOA questions first');
      return;
    }
    
    setError(null);
    onComplete({
      soaPlan: soaPrime,
      narrative: soaPrime?.narrative,
      riskNotes: soaPrime?.risk_notes
    });
  };

  // Get summary data
  const host = sessionData?.hosts?.[0];
  const audience = sessionData?.audience;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: EMZI_SPACING.xl, textAlign: 'center' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: EMZI_SPACING.sm, color: '#fff' }}>
          📋 {getText('title')}
        </h1>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>{getText('subtitle')}</p>
      </div>

      {/* Session Summary */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            📊 {getText('summaryTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            {/* Platform */}
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>{getText('platformLabel')}</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                {sessionData?.platform || 'TikTok'}
              </div>
            </div>
            
            {/* Duration */}
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>{getText('durationLabel')}</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                {sessionData?.durationMinutes || 60} min
              </div>
            </div>
            
            {/* CTA Interval */}
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>{getText('ctaIntervalLabel')}</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                Every {sessionData?.ctaInterval || 5} min
              </div>
            </div>
            
            {/* Objective */}
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>{getText('objectiveLabel')}</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#333', textTransform: 'capitalize' }}>
                {sessionData?.objective || 'Sales'}
              </div>
            </div>
            
            {/* Audience */}
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>{getText('audienceLabel')}</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                {audience?.ageRanges?.join(', ') || 'All ages'}
              </div>
            </div>
            
            {/* Panggilan */}
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>{getText('panggilanLabel')}</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                {sessionData?.panggilan === 'custom' ? sessionData?.panggilanCustom : sessionData?.panggilan || 'Korang'}
              </div>
            </div>
            
            {/* Nada */}
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', gridColumn: 'span 2' }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>{getText('nadaLabel')}</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                {sessionData?.nada === 'custom' ? sessionData?.nadaCustom : sessionData?.nada || 'Santai'}
              </div>
            </div>
          </div>
          
          {/* Host Info */}
          {host && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#E8F4FF', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#0e67a2', marginBottom: '4px' }}>🎤 Host Profile</div>
              <div style={{ fontSize: '14px', color: '#333' }}>
                <strong>{host.name}</strong> • {host.ageRange} • {host.gender === 'male' ? 'Lelaki' : 'Perempuan'} • {host.personality}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prime SOA Button */}
      {!isPrimed && (
        <div style={{ textAlign: 'center', marginBottom: EMZI_SPACING.lg }}>
          <button
            onClick={handlePrime}
            disabled={isPriming}
            style={{
              padding: '16px 32px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '16px',
              fontWeight: '700',
              cursor: isPriming ? 'not-allowed' : 'pointer',
              opacity: isPriming ? 0.7 : 1,
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            {isPriming ? getText('primingBtn') : getText('primeBtn')}
          </button>
        </div>
      )}

      {/* SOA Prime Result */}
      {isPrimed && soaPrime && (
        <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md, border: '2px solid #28a745' }}>
          <CardHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>✅</span>
              <CardTitle style={{ fontSize: '16px', fontWeight: '600', color: '#28a745' }}>
                {getText('readyText')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              🎯 {soaPrime.blueprint?.length || 5} {getText('questionsGenerated')}
            </p>
            
            {/* Preview Questions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {soaPrime.blueprint?.map((q, idx) => (
                <div
                  key={`item-${idx}`}
                  style={{
                    padding: '10px 14px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    borderLeft: '3px solid #667eea'
                  }}
                >
                  <span style={{ fontSize: '12px', color: '#667eea', fontWeight: '600' }}>
                    Q{idx + 1}: {q.domain?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Re-prime Button (if already primed) */}
      {isPrimed && (
        <div style={{ textAlign: 'center', marginBottom: EMZI_SPACING.md }}>
          <button
            onClick={handlePrime}
            disabled={isPriming}
            style={{
              padding: '10px 20px',
              background: '#fff',
              border: '1px solid #667eea',
              borderRadius: '8px',
              color: '#667eea',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ↻ Re-prime Questions
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: '14px',
            background: '#fff',
            border: '2px solid #0e67a2',
            borderRadius: '10px',
            color: '#0e67a2',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ← Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!isPrimed}
          style={{
            flex: 2,
            padding: '14px',
            background: isPrimed ? '#F7941E' : '#ccc',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: '600',
            cursor: isPrimed ? 'pointer' : 'not-allowed'
          }}
        >
          {getText('continueBtn')}
        </button>
      </div>
    </div>
  );
};

export default Step7ReviewPrime;
